import React, { useCallback } from 'react'
import { IconTrash } from '@tabler/icons-react'
import { enqueueSnackbar } from 'notistack'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from 'src/components/atoms/alert-dialog'
import Typography from 'src/components/atoms/Typography'
import { api } from 'src/api/root'
import { parentFolderPath } from 'src/enviroment/utils/parentFolderPath'
import { isInsideFolder } from 'src/enviroment/utils/isInsideFolder'

interface DeleteFolderModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  folderPath: string
  folderName: string
  documentCount: number
  onSelectFolder: (path: string | null) => void
  selectedFolderPath: string | null
}

const DeleteFolderModal: React.FC<DeleteFolderModalProps> = ({
  isOpen,
  onClose,
  projectId,
  folderPath,
  folderName,
  documentCount,
  onSelectFolder,
  selectedFolderPath,
}) => {
  const { mutateAsync: deleteFolder, isPending } = api.project.dataRoom.deleteFolder.useMutation({
    onSuccess: () => {
      if (isInsideFolder(selectedFolderPath, folderPath)) {
        onSelectFolder(parentFolderPath(folderPath))
      }
      onClose()
      enqueueSnackbar(`Folder "${folderName}" deleted successfully.`, { variant: 'success' })
    },
  })

  const handleDelete = useCallback(() => {
    deleteFolder({ projectId, folderPath, hasFiles: documentCount > 0 })
  }, [deleteFolder, projectId, folderPath, documentCount])

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && !isPending && onClose()}>
      <AlertDialogContent>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-light">
            <IconTrash className="h-6 w-6 text-red-ramo" />
          </div>
          <div className="flex flex-col justify-center gap-6">
            <AlertDialogTitle className="text-center font-inter text-xl font-bold text-gray-50">
              Delete Folder
            </AlertDialogTitle>
            <AlertDialogDescription asChild className="text-center font-inter text-base text-gray-50">
              <div>
                You&apos;re about to delete the{' '}
                <Typography variant="base-semibold" className="inline">
                  {folderName}
                </Typography>{' '}
                folder and all its contents. This action cannot be undone.
              </div>
            </AlertDialogDescription>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={onClose} className="w-40">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            isLoading={isPending}
            onClick={(e) => {
              e.preventDefault()
              handleDelete()
            }}
            className="w-40"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteFolderModal
